import useSWR from 'swr';
import {
  CardAccount,
  CustomerDetails,
  ExternalAccount,
  TransferDetails,
  VirtualAccountHistory,
  WalletDetails,
  WalletHistory,
} from '@thera-hr/api-sdk/api/resources/api/resources/thera/resources/bridge';
import axios from 'axios';

import { globalFetcherSettings } from 'lib/constants';
import { useTheraFernClient } from './useFern';
import { useUser } from 'context/user';

export const useBridgeCustomerDetails = ({ bridgeCustomerID }: { bridgeCustomerID: string }) => {
  const { theraFernApi } = useTheraFernClient();

  const getBridgeCustomerDetailsFetcher = async (): Promise<CustomerDetails> => {
    const response = await theraFernApi.thera.bridge.bridgeApi.getCustomerDetails(bridgeCustomerID);
    return response;
  };

  const { data, error, mutate, isLoading } = useSWR(
    () => (bridgeCustomerID ? ['getBridgeCustomerDetailsFetcher', bridgeCustomerID] : false),
    getBridgeCustomerDetailsFetcher,
    {
      onErrorRetry: (error) => {
        if (error.status === 404) return;
      },
      ...globalFetcherSettings,
    },
  );

  return {
    bridgeCustomer: data,
    mutateBridgeCustomer: mutate,
    isPending: isLoading,
    error,
  };
};

export const useBridgeGetWallet = ({ walletId }: { walletId: string }) => {
  const { theraFernApi } = useTheraFernClient();

  const getBridgeWalletFetcher = async (): Promise<WalletDetails> => {
    const response = await theraFernApi.thera.bridge.bridgeApi.getWalletDetails(walletId);
    return response;
  };

  const { data, error, mutate, isLoading } = useSWR(
    () => (walletId ? ['getBridgeWalletFetcher', walletId] : false),
    getBridgeWalletFetcher,
    {
      onErrorRetry: (error) => {
        if (error.status === 404) return;
      },
      ...globalFetcherSettings,
    },
  );

  return {
    bridgeWallet: data,
    mutateBridgeWallet: mutate,
    isPending: isLoading,
    error,
  };
};

export const useBridgeListWallets = ({ bridgeCustomerID }: { bridgeCustomerID: string }) => {
  const { theraFernApi } = useTheraFernClient();

  const getBridgeWalletListFetcher = async (): Promise<WalletDetails[]> => {
    const response = await theraFernApi.thera.bridge.bridgeApi.listWallets(bridgeCustomerID);
    return response;
  };

  const { data, error, mutate, isLoading } = useSWR(
    () => (bridgeCustomerID ? ['getBridgeWalletListFetcher', bridgeCustomerID] : false),
    getBridgeWalletListFetcher,
    {
      onErrorRetry: (error) => {
        if (error.status === 404) return;
      },
      ...globalFetcherSettings,
    },
  );

  return {
    bridgeWallets: data,
    mutateBridgeWalletList: mutate,
    isPending: isLoading,
    error,
  };
};

export const useBridgeListExternalAccounts = ({ bridgeCustomerID }: { bridgeCustomerID: string }) => {
  const { isLoading: isUserLoading } = useUser();
  const { theraFernApi } = useTheraFernClient();

  const getBridgeListExternalAccountsFetcher = async (): Promise<ExternalAccount[]> => {
    const response = await theraFernApi.thera.bridge.bridgeApi.listExternalAccounts(bridgeCustomerID);
    return response;
  };

  const { data, error, mutate, isLoading } = useSWR(
    () => (bridgeCustomerID ? ['getBridgeListExternalAccountsFetcher', bridgeCustomerID] : false),
    getBridgeListExternalAccountsFetcher,
    {
      onErrorRetry: (error) => {
        if (error.status === 404) return;
      },
      ...globalFetcherSettings,
    },
  );

  return {
    bridgeExternalAccounts: data,
    mutateBridgeAccounts: mutate,
    isPending: isLoading || isUserLoading,
    error,
  };
};

export const useBridgeListTransfers = ({ bridgeCustomerID }: { bridgeCustomerID: string }) => {
  const { theraFernApi } = useTheraFernClient();

  const getBridgeListTransfersFetcher = async (): Promise<TransferDetails[]> => {
    const response = await theraFernApi.thera.bridge.bridgeApi.listTransfers(bridgeCustomerID);
    return response;
  };

  const { data, error, mutate, isLoading } = useSWR(
    () => (bridgeCustomerID ? ['getBridgeListTransfersFetcher', bridgeCustomerID] : false),
    getBridgeListTransfersFetcher,
    {
      onErrorRetry: (error) => {
        if (error.status === 404) return;
      },
      ...globalFetcherSettings,
    },
  );

  return {
    bridgeTransfers: data,
    mutateBridgeTransfers: mutate,
    isPending: isLoading,
    error,
  };
};

interface VirtualAccountHistoryResponse {
  virtualAccountsHistory: VirtualAccountHistory[];
  isPending: boolean;
  count?: number;
}

/**
 * Hook to fetch virtual accounts history
 * @param userId - User ID to fetch history for
 * @param refreshInterval - Optional auto-refresh interval in ms (disabled by default)
 */
export const useBridgeVirtualAccountsHistory = ({
  userId,
  refreshInterval,
}: {
  userId?: string;
  refreshInterval?: number;
}): VirtualAccountHistoryResponse => {
  const { theraFernApi } = useTheraFernClient();
  const { data: virtualAccountsHistory, isLoading } = useSWR(
    userId ? `/bridge/users/${userId}/virtual-accounts-history` : null,
    () => theraFernApi.thera.bridge.bridgeApi.listHistoryOfVirtualAccounts(userId!),
    {
      refreshInterval, // Only refresh if explicitly enabled
      ...globalFetcherSettings,
    },
  );

  return {
    virtualAccountsHistory: virtualAccountsHistory?.data,
    isPending: isLoading,
    count: virtualAccountsHistory?.count,
  };
};

interface WalletTransactionsHistoryResponse {
  walletTransactions: WalletHistory[];
  isPending: boolean;
  count?: number;
}

/**
 * Hook to fetch wallet transactions history
 * @param walletId - Wallet ID to fetch transactions for
 * @param refreshInterval - Optional auto-refresh interval in ms (disabled by default)
 */
export const useBridgeWalletTransactionsHistory = ({
  walletId,
  refreshInterval,
}: {
  walletId?: string;
  refreshInterval?: number;
}): WalletTransactionsHistoryResponse => {
  const { theraFernApi } = useTheraFernClient();
  const { data: walletTransactionsHistory, isLoading } = useSWR(
    walletId ? `/bridge/wallets/${walletId}/transactions` : null,
    () => theraFernApi.thera.bridge.bridgeApi.walletsTransactionHistory(walletId!),
    {
      refreshInterval, // Only refresh if explicitly enabled
      ...globalFetcherSettings,
    },
  );

  return {
    walletTransactions: walletTransactionsHistory?.data,
    isPending: isLoading,
    count: walletTransactionsHistory?.count,
  };
};

/**
 * Hook to fetch card account details including pin status
 */
export const useBridgeCardAccount = ({
  customerId,
  cardAccountId,
}: {
  customerId?: string;
  cardAccountId?: string;
}) => {
  const { theraFernApi } = useTheraFernClient();

  const getCardAccountFetcher = async (): Promise<CardAccount> => {
    const response = await theraFernApi.thera.bridge.bridgeApi.retrieveCardById(customerId!, cardAccountId!);
    return response;
  };

  const { data, error, mutate, isLoading } = useSWR(
    () => (customerId && cardAccountId ? ['getBridgeCardAccount', customerId, cardAccountId] : false),
    getCardAccountFetcher,
    {
      onErrorRetry: (error) => {
        if (error.status === 404) return;
      },
      ...globalFetcherSettings,
    },
  );

  return {
    cardAccount: data,
    mutateCardAccount: mutate,
    isPending: isLoading,
    error,
  };
};

/**
 * Hook to fetch list of countries from Bridge API
 */
export const useBridgeSubDivisions = () => {
  const { data, error, mutate, isLoading } = useSWR(
    'getBridgeCountries',
    async () => {
      const response = await axios.get('https://api.sandbox.bridge.xyz/v0/lists/countries', {
        headers: {
          'Api-Key': process.env.NEXT_PUBLIC_BRIDGE_API_KEY || 'sk-test-4c3ee2db27138b764c785285d13aedbd',
        },
      });
      return response.data;
    },
    {
      onErrorRetry: (error) => {
        if (error.status === 404) return;
      },
      ...globalFetcherSettings,
    },
  );

  return {
    countries: data,
    mutateCountries: mutate,
    isPending: isLoading,
    error,
  };
};
